import { CdkCell, CdkCellDef, CdkColumnDef, CdkHeaderCell, CdkHeaderCellDef } from '@angular/cdk/table';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SparePart } from '@ecs-features/result/spare-parts-result/spare-parts.interface';
import { TranslatePipe } from '@ngx-translate/core';
import {
    BaseTableModule,
    DefaultTooltipComponent,
    HeaderConfigService,
    NgUseDirective,
    NumbersOnlyDirective,
    SelectableRow,
    TooltipDirective,
} from '@os-components';
import {
    SewButton,
    SewIcon,
    SewLink,
    SewLoaderMini,
    SewTableInput,
} from '@sew/ui-components-angular/standalone';
import { PartAmounts } from 'src/app/components/part-table/part-table.component';
import {
    removeSpecificColumnsIfSEWParterSite,
    selectedRowsCompareFunction,
} from 'src/app/components/part-table/part-table.helper';
import { BasketItemType } from '../../../../../services/basket.service';
import { PartTableSkeletonComponent } from '../../../../part-table-skeleton/part-table-skeleton.component';
import { AvailabilityCellComponent } from '../../../../part-table/components/availability-cell/availability-cell.component';
import { PartRow } from '../../../../part-table/part-table.interface';
import { BasketItemRow } from '../../part-configuration-wizard.service';

@Component({
    selector: 'ecs-cross-selling-table',
    templateUrl: './cross-selling-table.component.html',
    styleUrls: ['./cross-selling-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SewIcon,
        SewButton,
        SewTableInput,
        SewLoaderMini,
        SewLink,
        BaseTableModule,
        CdkColumnDef,
        CdkHeaderCellDef,
        CdkHeaderCell,
        CdkCellDef,
        CdkCell,
        TooltipDirective,
        ReactiveFormsModule,
        NumbersOnlyDirective,
        FormsModule,
        NgUseDirective,
        AvailabilityCellComponent,
        PartTableSkeletonComponent,
        TranslatePipe,
        CommonModule,
        DefaultTooltipComponent,
    ],
})
export class CrossSellingTableComponent implements OnInit {
    @Input() public set items(items: SelectableRow<SparePart>[]) {
        this.partRows = items;
        this.partAmounts = this.setupPartAmounts(items.map((item) => item.rowData));
    }

    @Input() public set columns(columns: string[]) {
        if (columns.length > 0) {
            this.displayedColumns = removeSpecificColumnsIfSEWParterSite(
                this.headerconfigService.getCountryShort(),
                columns
            );
        }
    }

    @Input() public hideMarkedBar = false;
    @Input() public showCheckboxColumn = true;
    @Input() public showPartGroupHeaderAsSkeleton = false;
    @Input() public userLoggedIn = false;
    @Input() public uniqueContextSparePartId = '';

    @Input() public checkedRows?: PartRow[];

    @Output() public rowSelect = new EventEmitter<BasketItemRow[]>();

    public selectedRows: SelectableRow<SparePart>[] = [];
    public displayedColumns?: string[];

    // can set Type to SparePart, because its always SparePart in gear oil cross selling
    public partRows: SelectableRow<SparePart>[] = [];
    public partAmounts: PartAmounts = {};
    public loginUrl = '';
    public selectedRowsCompareFunction = selectedRowsCompareFunction;

    public constructor(private headerconfigService: HeaderConfigService) {}

    public ngOnInit() {
        if (!this.displayedColumns) {
            this.columns = ['name', 'materialNumber', 'amount', 'availability'];
        }
    }

    public onRowSelect(event: SelectableRow<SparePart>[]) {
        this.selectedRows = event;
        this.rowSelect.emit(event.map(this.createBasketItemByRow));
    }

    public createBasketItems() {
        return this.selectedRows.map(this.createBasketItemByRow);
    }

    private createBasketItemByRow = (row: SelectableRow<SparePart>) =>
        ({
            row,
            basketItem: {
                type: BasketItemType.SPARE_PART,
                productId: row.rowData.id,
                quantity: this.partAmounts[row.rowData.id],
            },
        }) as BasketItemRow;

    private setupPartAmounts(spareParts: SparePart[]): PartAmounts {
        return spareParts.reduce(
            (previousPart, part) => ({
                ...previousPart,
                [part.id]: this.partAmounts[part.id]
                    ? this.partAmounts[part.id]
                    : (part.installedQuantity ?? 1),
            }),
            {}
        );
    }
}
